import { useCallback } from 'react'

import {
  type Query,
  QueryKey,
  type QueryOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'
import {
  cleanFileName,
  downloadFile,
  isUnhydratedFile,
  isWordFile,
} from '@/helpers/file'

import { fileQueryKeys } from '@/hooks/files/types'
import {
  STILL_SAVING_MESSAGE,
  saveEditorEditsFirst,
} from '@/hooks/files/useFileVersions'

import type { PaginatedResponse } from '@/types/Api'
import type { File, PotentiallyUnhydratedFile } from '@/types/File'
import type { WorkingCopyCheckpointOutcome } from '@/types/file/FileVersion'
import type { UUID } from '@/types/Id'

const MAX_BULK_FILE_IDS = 1000

interface UseFileArgs<T> {
  fileId: string | null
  staleTime?: number
  refetchOnWindowFocus?: boolean
  refetchInterval?:
    | number
    | false
    | ((
        query: Query<File | null, Error, File | null, QueryKey>,
      ) => number | false | undefined)
  select?: (data: File) => T
}

export const fetchFile = async (
  fileId: string,
  accessToken: string | undefined | null,
  forceNewPresignedUrl: boolean = false,
): Promise<File> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const url = `${API_URL}/api/v1/files/${fileId}?force_new_presigned_url=${forceNewPresignedUrl}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return convertDatesInObject(await res.json())
}

export const fetchFilesBulk = async (
  fileIds: UUID[],
  accessToken: string | undefined | null,
): Promise<File[]> => {
  if (fileIds.length === 0) {
    return []
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const hydratedFiles: File[] = []
  for (let index = 0; index < fileIds.length; index += MAX_BULK_FILE_IDS) {
    const batchFileIds = fileIds.slice(index, index + MAX_BULK_FILE_IDS)
    const res = await fetch(`${API_URL}/api/v1/files/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ file_ids: batchFileIds }),
    })
    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: res,
      })
    }
    const payload = convertDatesInObject(
      await res.json(),
    ) as PaginatedResponse<File>
    const filesById = new Map(payload.data.map((file) => [file.id, file]))

    hydratedFiles.push(
      ...batchFileIds.flatMap((fileId) => {
        const file = filesById.get(fileId)
        return file ? [file] : []
      }),
    )
  }

  return hydratedFiles
}

/**
 * Whether the bytes a read would get are the latest: no save it waited on is
 * still running. A read that went ahead would hand over pre-edit bytes as current.
 */
const editsSettled = (
  outcomes: ReadonlyArray<WorkingCopyCheckpointOutcome | null>,
): boolean => {
  if (!outcomes.includes('pending')) {
    return true
  }
  toast.error(STILL_SAVING_MESSAGE)
  return false
}

/**
 * The save before a read, for every Word file among many, together. False when
 * a save is still running and the read should not go ahead.
 */
export const saveWordEditsFirst = async (
  files: ReadonlyArray<Pick<File, 'id' | 'name' | 'content_type'>>,
  accessToken: string | undefined | null,
): Promise<boolean> =>
  editsSettled(
    await Promise.all(
      files
        .filter(isWordFile)
        .map((file) => saveEditorEditsFirst(file.id, accessToken)),
    ),
  )

/**
 * Download a file that may have come from a URL-less list endpoint: hydrates on
 * demand when the record has no presigned URLs, and again when a save moved the
 * file, since the links in hand point at the bytes it had before.
 */
export const downloadPotentiallyUnhydratedFile = async (
  file: PotentiallyUnhydratedFile,
  accessToken: string | undefined | null,
): Promise<void> => {
  const saved = isWordFile(file)
    ? await saveEditorEditsFirst(file.id, accessToken)
    : null
  if (!editsSettled([saved])) {
    return
  }
  const hydratedFile =
    saved === 'saved' || isUnhydratedFile(file)
      ? await fetchFile(file.id, accessToken)
      : file
  await downloadFile(
    hydratedFile.url,
    cleanFileName(hydratedFile.name),
    hydratedFile.download_url,
  )
}

/** downloadPotentiallyUnhydratedFile for a component with no token in hand. */
export const useDownloadStoredFile = () => {
  const auth = useAuthInfo()
  return useCallback(
    (file: PotentiallyUnhydratedFile) =>
      downloadPotentiallyUnhydratedFile(file, auth?.accessToken),
    [auth?.accessToken],
  )
}

const FILE_RETRY_COUNT = 3

/** A file that is gone, or that this reader may not have: asking again gets the same answer. */
const isFileReadRefused = (error: Error): boolean => {
  const status = error instanceof SweetspotError ? error.cause?.status : null
  return status === 403 || status === 404
}

export const useFile = <T = File>({
  fileId,
  staleTime = FILE_STALE_TIME,
  refetchOnWindowFocus = true,
  refetchInterval,
  select,
}: UseFileArgs<T>) => {
  const user = useAuthInfo()

  const selectFn = useCallback(
    (data: File | null) => {
      if (!data) {
        return null
      }
      return select ? select(data) : (data as unknown as T)
    },
    [select],
  )

  return useQuery({
    queryFn: () =>
      !fileId || fileId.startsWith('local')
        ? null
        : fetchFile(fileId, user?.accessToken),
    queryKey: fileQueryKeys.file({ fileId: fileId ?? '' }),
    staleTime,
    enabled: !!fileId,
    refetchOnWindowFocus,
    refetchInterval,
    retry: (failureCount, error) =>
      !isFileReadRefused(error) && failureCount < FILE_RETRY_COUNT,
    select: selectFn,
  })
}

interface UseFilePreviewArgs {
  fileId: string | null
  enabled?: boolean
}

export const useFilePreview = ({
  fileId,
  enabled = true,
}: UseFilePreviewArgs) => {
  const user = useAuthInfo()

  return useQuery({
    queryFn: () =>
      !fileId || fileId.startsWith('local')
        ? null
        : fetchFile(fileId, user?.accessToken, true),
    queryKey: fileQueryKeys.preview({ fileId: fileId ?? '' }),
    staleTime: 0,
    enabled: enabled && !!fileId,
    refetchOnWindowFocus: false,
  })
}

export const useSetFileCache = () => {
  const queryClient = useQueryClient()

  const setFileCache = useCallback(
    (files: File[]) => {
      for (const file of files) {
        queryClient.setQueryData(fileQueryKeys.file({ fileId: file.id }), file)
      }
    },
    [queryClient],
  )

  return setFileCache
}
